import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Square, ArrowUp, ArrowDown } from "lucide-react";
import { ElementLabel } from "./ElementLabel";
import { AlignmentComplete } from "./AlignmentComplete";
import IlluminatedBeing from "./IlluminatedBeing";
import { CHAKRAS } from "../data/chakras";
import { audioEngine } from "../lib/audioEngine";
import { useFocusStyle } from "../lib/focusScale";
import { useSavedState } from "../lib/savedState";

const DURATIONS = [
  { label: "6s", v: 6 },
  { label: "12s", v: 12 },
  { label: "20s", v: 20 },
];

const PRESETS = [
  { id: "solfeggio", label: "Pure Tone", engine: "solfeggio" },
  { id: "bowl", label: "Singing Bowl", engine: "bowl" },
  { id: "binaural", label: "Binaural Theta", engine: "binaural", beat: 6 },
];

const OCTAVES = [
  { id: "low", label: "Low", mult: 0.5 },
  { id: "mid", label: "Mid", mult: 1 },
  { id: "high", label: "High", mult: 2 },
];

export default function MeditationMode({ volume, focus, onEngage }) {
  const focusStyle = useFocusStyle(focus);
  const [index, setIndex] = useState(-1); // -1 = idle
  const [running, setRunning] = useState(false);
  const [manual, setManual] = useState(false); // manual single-chakra loop
  const [perChakra, setPerChakra] = useSavedState("meditation.seconds", 12);
  const [preset, setPreset] = useSavedState("meditation.sound", "binaural");
  const [octave, setOctave] = useSavedState("meditation.octave", "low");
  const [completed, setCompleted] = useState(false);
  const [geo, setGeo] = useState("seed");
  const [direction, setDirection] = useSavedState("meditation.direction", "up");
  const dirRef = useRef("up");
  const timer = useRef(null);
  const completeTimer = useRef(null);
  const flowerTimer = useRef(null);
  const manualTimer = useRef(null);
  const perChakraRef = useRef(perChakra);
  const presetRef = useRef(preset);
  const octaveRef = useRef(octave);
  const audioRef = useRef(null);

  useEffect(() => {
    perChakraRef.current = perChakra;
  }, [perChakra]);
  useEffect(() => {
    presetRef.current = preset;
  }, [preset]);
  useEffect(() => {
    octaveRef.current = octave;
  }, [octave]);

  const octMult = (OCTAVES.find((o) => o.id === octave) || OCTAVES[1]).mult;

  const activeChakra = index >= 0 ? CHAKRAS[index] : null;

  // live-update the synth tone when preset/octave changes mid-play (skip uploaded tracks)
  useEffect(() => {
    if (index >= 0 && (running || manual)) {
      const cfg = PRESETS.find((p) => p.id === preset) || PRESETS[1];
      audioEngine.play(CHAKRAS[index].frequency * octMult, { preset: cfg.engine, beat: cfg.beat });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, octave]);

  const playStep = (i) => {
    const chakra = CHAKRAS[i];
    audioEngine.setVolume(volume);
    const cfg = PRESETS.find((p) => p.id === presetRef.current) || PRESETS[1];
    const mult = (OCTAVES.find((o) => o.id === octaveRef.current) || OCTAVES[1]).mult;
    audioEngine.play(chakra.frequency * mult, { preset: cfg.engine, beat: cfg.beat });
    audioEngine.chime(chakra.frequency * (OCTAVES.find((o) => o.id === octaveRef.current) || OCTAVES[1]).mult);
  };

  const advance = (k) => {
    if (k >= CHAKRAS.length) {
      stop(true);
      return;
    }
    const i = dirRef.current === "down" ? CHAKRAS.length - 1 - k : k;
    setIndex(i);
    setGeo("seed"); // Seed of Life when beginning this chakra
    playStep(i);
    // bloom into Flower of Life shortly before this chakra completes
    clearTimeout(flowerTimer.current);
    flowerTimer.current = setTimeout(() => setGeo("flower"), Math.max(600, perChakra * 1000 - 3000));
    timer.current = setTimeout(() => advance(k + 1), perChakra * 1000);
  };

  const start = () => {
    onEngage?.();
    stopManual();
    setCompleted(false);
    setRunning(true);
    dirRef.current = direction;
    advance(0);
  };

  // Manual single-chakra loop (click an orb without starting the journey).
  const manualLoop = (i) => {
    const secs = perChakraRef.current;
    setIndex(i);
    setGeo("seed");
    playStep(i);
    clearTimeout(flowerTimer.current);
    flowerTimer.current = setTimeout(() => setGeo("flower"), Math.max(600, secs * 1000 - 3000));
    // play a single cycle based on the chosen seconds, then stop
    manualTimer.current = setTimeout(() => {
      audioEngine.stop(0.6);
      if (audioRef.current) audioRef.current.pause();
      setManual(false);
      setIndex(-1);
      setGeo("seed");
    }, secs * 1000);
  };

  const stopManual = () => {
    clearTimeout(manualTimer.current);
    clearTimeout(flowerTimer.current);
    if (manual) {
      audioEngine.stop(0.4);
      if (audioRef.current) audioRef.current.pause();
      setIndex(-1);
      setGeo("seed");
    }
    setManual(false);
  };

  const handleOrbClick = (c) => {
    if (running) return; // journey in progress
    const i = CHAKRAS.findIndex((x) => x.id === c.id);
    if (manual && index === i) {
      stopManual(); // toggle off
      return;
    }
    clearTimeout(manualTimer.current);
    clearTimeout(flowerTimer.current);
    setCompleted(false);
    setManual(true);
    manualLoop(i);
  };

  const stop = (finished = false) => {
    clearTimeout(timer.current);
    clearTimeout(flowerTimer.current);
    clearTimeout(manualTimer.current);
    setRunning(false);
    setManual(false);
    audioEngine.stop(0.5);
    if (audioRef.current) audioRef.current.pause();
    if (finished) {
      audioEngine.chime(963);
      setCompleted(true);
      setGeo("flower");
      clearTimeout(completeTimer.current);
      completeTimer.current = setTimeout(() => setCompleted(false), 9000);
    } else {
      setGeo("seed");
    }
    setIndex(-1);
  };

  useEffect(() => () => {
    clearTimeout(timer.current);
    clearTimeout(completeTimer.current);
    clearTimeout(flowerTimer.current);
    clearTimeout(manualTimer.current);
    audioEngine.stop(0.3);
    if (audioRef.current) audioRef.current.pause();
  }, []);

  return (
    <div className="relative flex flex-1 flex-col items-center">
      <audio ref={audioRef} data-testid="healing-audio" />
      {/* Mobile cues: own row above the figure so they never cover the head */}
      <div className={`pointer-events-none absolute inset-x-0 z-10 h-[68px] flex-col items-center justify-center text-center sm:hidden ${focus ? "focus-cue-top flex" : "top-0 flex"}`} data-testid="meditation-mobile-cues">
        {activeChakra && (
          <>
            <ElementLabel chakra={activeChakra} testid="meditation-element-mobile" className="text-[11px]" />
            <p className="font-serif text-2xl text-white">{activeChakra.english}</p>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300/80">
              {`${Math.round(activeChakra.frequency * octMult)} Hz · Note ${activeChakra.note}`}
            </p>
          </>
        )}
      </div>
      <div className="relative -mb-6 mt-3 flex w-full flex-1 items-center justify-center sm:mb-0 sm:mt-0">
        <div className="relative" style={focusStyle}>
          <IlluminatedBeing
            activeChakraId={activeChakra?.id}
            onChakraClick={handleOrbClick}
            breathScale={1}
            auraColor={activeChakra ? activeChakra.glow : "rgba(120,150,255,0.4)"}
            healingProgress={index}
            geometry={geo}
            bloom={geo === "flower"}
            afterglow={completed}
          />
        </div>
        {activeChakra && (
          <>
            {/* Left cues in the dark space, outside the aura sphere */}
            <div
              className="absolute left-4 top-1/2 hidden -translate-y-1/2 text-left sm:block sm:left-12 lg:left-24"
              data-testid="healing-active-label"
            >
              <ElementLabel chakra={activeChakra} testid="meditation-element" className="mb-2" />
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-300/80">
                {activeChakra.english}
              </p>
              <p className="mt-1 font-mono text-xs uppercase tracking-[0.3em] text-cyan-300/55">
                {`${Math.round(activeChakra.frequency * octMult)} Hz · Note ${activeChakra.note}`}
              </p>
              <p className="mt-6 font-serif text-4xl tracking-widest text-white/90 lg:text-5xl">
                {activeChakra.name}
              </p>
              <AnimatePresence mode="wait">
                <motion.p
                  key={activeChakra.id}
                  data-testid="meditation-affirmation-side"
                  className="mt-8 hidden max-w-[20rem] font-serif text-xl italic leading-relaxed text-slate-200 lg:block"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.8 }}
                >
                  "{activeChakra.affirmation}"
                </motion.p>
              </AnimatePresence>
            </div>
            {/* Mobile cues moved above the stage */}
          </>
        )}
        {!activeChakra && !focus && (
          <div
            className="absolute left-4 top-1/2 hidden max-w-[12rem] -translate-y-1/2 text-left lg:block lg:left-24"
            data-testid="healing-idle-hint"
          >
            <p className="font-mono text-[11px] uppercase leading-relaxed tracking-[0.3em] text-slate-500">
              Tap any chakra to play one cycle · or Begin for all seven
            </p>
          </div>
        )}

        <AlignmentComplete show={completed} direction={dirRef.current} onDismiss={() => setCompleted(false)} />
      </div>

      {focus && activeChakra && (
        <div className="focus-under-figure pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-6 sm:bottom-10 lg:hidden">
          <AnimatePresence mode="wait">
            <motion.p
              key={activeChakra.id}
              data-testid="meditation-focus-affirmation"
              className="max-w-2xl text-center font-serif text-lg italic text-slate-100 sm:text-2xl"
              style={{ textShadow: "0 0 18px rgba(0,0,0,0.9), 0 0 6px rgba(0,0,0,0.8)" }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.8 }}
            >
              "{activeChakra.affirmation}"
            </motion.p>
          </AnimatePresence>
        </div>
      )}
      <div className={`relative w-full max-w-3xl rounded-2xl border border-white/10 bg-slate-950/70 p-4 sm:p-6 backdrop-blur-xl ${focus ? "hidden" : ""}`}>
        {!activeChakra && (
          <p
            className="mb-3 sm:mb-4 text-center font-mono text-[11px] uppercase leading-relaxed tracking-[0.25em] text-slate-400 lg:hidden"
            data-testid="healing-idle-hint-mobile"
          >
            Tap any chakra to play one cycle · or Begin for all seven
          </p>
        )}
        {activeChakra && (
          <AnimatePresence mode="wait">
            <motion.p
              key={activeChakra.id}
              data-testid="meditation-affirmation"
              className="mb-3 sm:mb-4 text-center font-serif text-base sm:text-lg italic text-slate-200 lg:hidden"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.6 }}
            >
              "{activeChakra.affirmation}"
            </motion.p>
          </AnimatePresence>
        )}
        <div className="mb-3 flex items-center justify-center gap-3 sm:mb-4">
          {!running ? (
            <button
              data-testid="healing-start"
              onClick={start}
              className="flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 px-6 py-2.5 text-sm font-medium text-white shadow-lg shadow-emerald-500/30 transition-transform hover:scale-105"
            >
              <Play size={16} /> Begin
            </button>
          ) : (
            <button
              data-testid="healing-stop"
              onClick={() => stop(false)}
              className="flex items-center gap-2 rounded-full bg-white/10 px-6 py-2.5 text-sm font-medium text-white transition-transform hover:scale-105"
            >
              <Square size={15} /> Stop
            </button>
          )}
          <div className="flex gap-1 justify-self-start rounded-full border border-white/10 bg-white/5 p-1">
            {[
              { id: "up", label: "Ascend", Icon: ArrowUp },
              { id: "down", label: "Descend", Icon: ArrowDown },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                data-testid={`meditation-direction-${id}`}
                onClick={() => setDirection(id)}
                disabled={running}
                className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed ${direction === id ? "bg-white/15 text-white" : "text-slate-400 hover:text-white disabled:hover:text-slate-400"}`}
              >
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
        </div>
        <p className="mb-3 sm:mb-4 text-center font-mono text-[11px] leading-relaxed text-slate-500">
          {/* mobile & tablet: 4 centered lines */}
          <span className="lg:hidden">
            Focus Pocus. Attention + Intention
            <br />
            Calibration of Energy Body
            <br />
            Through Body, Mind, and Spirit.
            <br />
            Matter, Sound, Light
          </span>
          {/* desktop */}
          <span className="hidden lg:inline">
            Focus Pocus. Attention + Intention = Calibration of Energy Body
            <br />
            Through Body, Mind and Spirit. Matter, Sound, Light.
          </span>
        </p>
        <div className="flex items-center justify-center gap-2 sm:gap-3">
          <div className="flex-1">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-rose-500 via-emerald-400 to-violet-400 transition-all duration-700"
                style={{ width: `${index >= 0 ? ((running && dirRef.current === "down" ? CHAKRAS.length - index : index + 1) / CHAKRAS.length) * 100 : 0}%` }}
                data-testid="healing-progress"
              />
            </div>
          </div>
        </div>

        <div className="mt-3 sm:mt-5 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          <div className="flex gap-1 rounded-full border border-white/10 bg-white/5 p-1">
            {DURATIONS.map((d) => (
              <button
                key={d.v}
                data-testid={`healing-duration-${d.v}`}
                onClick={() => setPerChakra(d.v)}
                className={`rounded-full px-3 py-1.5 text-xs transition-colors ${perChakra === d.v ? "bg-white/15 text-white" : "text-slate-400 hover:text-white"}`}
              >
                {d.label}
              </button>
            ))}
          </div>

          <div className="flex gap-1 rounded-full border border-white/10 bg-white/5 p-1">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                data-testid={`healing-preset-${p.id}`}
                onClick={() => setPreset(p.id)}
                className={`rounded-full px-3 py-1.5 text-xs transition-colors ${preset === p.id ? "bg-white/15 text-white" : "text-slate-400 hover:text-white"}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1">
            <span className="px-2 text-[10px] uppercase tracking-widest text-slate-500">Octave</span>
            {OCTAVES.map((o) => (
              <button
                key={o.id}
                data-testid={`healing-octave-${o.id}`}
                onClick={() => setOctave(o.id)}
                className={`rounded-full px-3 py-1.5 text-xs transition-colors ${octave === o.id ? "bg-white/15 text-white" : "text-slate-400 hover:text-white"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
